/**
 * CR-006 Phase 2 Feature 1 (U211): Self-Inspection Custom Hooks
 *
 * Provides reusable hooks for self-inspection customer portal:
 * - Session management
 * - OTP authentication
 * - Media upload with progress tracking
 * - Status polling
 */

import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api-client';

// ==================== Types ====================

interface SessionState {
  sessionToken: string | null;
  loading: boolean;
  error: string | null;
  sessionData: Record<string, unknown> | null;
}

interface OTPState {
  requesting: boolean;
  verifying: boolean;
  error: string | null;
  otpSent: boolean;
  verified: boolean;
  deliveryMethod: 'email' | 'sms';
}

interface UploadState {
  files: File[];
  uploading: boolean;
  progress: number;
  error: string | null;
  uploadedFiles: Record<string, unknown>[];
}

interface StatusState {
  loading: boolean;
  error: string | null;
  status: string | null;
  totalFiles: number;
  maxFiles: number;
  reportDownloadUrl: string | null;
}

// ==================== useSelfInspectionSession Hook ====================

/**
 * Hook for managing self-inspection session
 * Loads session details from backend using session token
 */
export function useSelfInspectionSession(sessionToken: string | null) {
  const [state, setState] = useState<SessionState>({
    sessionToken,
    loading: false,
    error: null,
    sessionData: null,
  });

  useEffect(() => {
    if (!sessionToken) {
      setState(prev => ({ ...prev, sessionData: null }));
      return;
    }

    async function loadSession() {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        const data = await apiClient.getSelfInspectionStatus(sessionToken!);
        setState(prev => ({
          ...prev,
          loading: false,
          sessionData: data,
        }));
      } catch (err: unknown) {
        const errorMessage = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail || 'Failed to load session';
        setState(prev => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    }

    loadSession();
  }, [sessionToken]);

  return state;
}

// ==================== useSelfInspectionOTP Hook ====================

/**
 * Hook for OTP request and verification
 * Handles both email and SMS OTP delivery
 */
export function useSelfInspectionOTP(sessionToken: string | null) {
  const [state, setState] = useState<OTPState>({
    requesting: false,
    verifying: false,
    error: null,
    otpSent: false,
    verified: false,
    deliveryMethod: 'email',
  });

  const requestOTP = useCallback(
    async (deliveryMethod: 'email' | 'sms' = 'email') => {
      if (!sessionToken) {
        setState(prev => ({ ...prev, error: 'Session token is required' }));
        return false;
      }

      setState(prev => ({
        ...prev,
        requesting: true,
        error: null,
        deliveryMethod,
      }));

      try {
        await apiClient.requestSelfInspectionOTP(sessionToken, deliveryMethod);
        setState(prev => ({
          ...prev,
          requesting: false,
          otpSent: true,
        }));
        return true;
      } catch (err: unknown) {
        const errorMessage = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail || 'Failed to send OTP';
        setState(prev => ({
          ...prev,
          requesting: false,
          error: errorMessage,
        }));
        return false;
      }
    },
    [sessionToken]
  );

  const verifyOTP = useCallback(
    async (otpCode: string) => {
      if (!sessionToken) {
        setState(prev => ({ ...prev, error: 'Session token is required' }));
        return null;
      }

      if (!otpCode || otpCode.length !== 6) {
        setState(prev => ({ ...prev, error: 'Invalid OTP code' }));
        return null;
      }

      setState(prev => ({ ...prev, verifying: true, error: null }));

      try {
        const result = await apiClient.verifySelfInspectionOTP(sessionToken, otpCode);
        setState(prev => ({
          ...prev,
          verifying: false,
          verified: true,
        }));
        return result;
      } catch (err: unknown) {
        const errorMessage = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail || 'Invalid OTP code';
        setState(prev => ({
          ...prev,
          verifying: false,
          error: errorMessage,
        }));
        return null;
      }
    },
    [sessionToken]
  );

  return {
    ...state,
    requestOTP,
    verifyOTP,
  };
}

// ==================== useSelfInspectionUpload Hook ====================

/**
 * Hook for media upload with progress tracking
 * Handles file validation and upload to backend
 */
export function useSelfInspectionUpload(sessionToken: string | null) {
  const [state, setState] = useState<UploadState>({
    files: [],
    uploading: false,
    progress: 0,
    error: null,
    uploadedFiles: [],
  });

  const addFiles = useCallback((newFiles: File[]) => {
    setState(prev => {
      const maxFiles = 10;
      const currentCount = prev.files.length;
      const availableSlots = maxFiles - currentCount;

      if (availableSlots <= 0) {
        return {
          ...prev,
          error: `Maximum ${maxFiles} files allowed`,
        };
      }

      const filesToAdd = newFiles.slice(0, availableSlots);

      // Validate file sizes
      const maxImageSize = 5 * 1024 * 1024; // 5MB
      const maxVideoSize = 50 * 1024 * 1024; // 50MB

      const invalidFiles = filesToAdd.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (isImage && file.size > maxImageSize) return true;
        if (isVideo && file.size > maxVideoSize) return true;
        if (!isImage && !isVideo) return true;

        return false;
      });

      if (invalidFiles.length > 0) {
        return {
          ...prev,
          error: 'Some files exceed size limits (Images: 5MB, Videos: 50MB)',
        };
      }

      return {
        ...prev,
        files: [...prev.files, ...filesToAdd],
        error: null,
      };
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      error: null,
    }));
  }, []);

  const clearFiles = useCallback(() => {
    setState(prev => ({
      ...prev,
      files: [],
      error: null,
    }));
  }, []);

  const uploadFiles = useCallback(async () => {
    if (!sessionToken) {
      setState(prev => ({ ...prev, error: 'Session token is required' }));
      return false;
    }

    if (state.files.length === 0) {
      setState(prev => ({ ...prev, error: 'No files to upload' }));
      return false;
    }

    setState(prev => ({ ...prev, uploading: true, progress: 0, error: null }));

    try {
      // Upload files
      const result = await apiClient.uploadSelfInspectionMedia(sessionToken, state.files);

      setState(prev => ({
        ...prev,
        uploading: false,
        progress: 100,
        uploadedFiles: result.uploads || [],
        files: [], // Clear files after successful upload
      }));

      return true;
    } catch (err: unknown) {
      const errorMessage = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail || 'Failed to upload files';
      setState(prev => ({
        ...prev,
        uploading: false,
        progress: 0,
        error: errorMessage,
      }));
      return false;
    }
  }, [sessionToken, state.files]);

  return {
    ...state,
    addFiles,
    removeFile,
    clearFiles,
    uploadFiles,
  };
}

// ==================== useSelfInspectionStatus Hook ====================

/**
 * Hook for checking upload status and progress
 * Polls backend for current status
 */
export function useSelfInspectionStatus(sessionToken: string | null, autoRefresh: boolean = false) {
  const [state, setState] = useState<StatusState>({
    loading: false,
    error: null,
    status: null,
    totalFiles: 0,
    maxFiles: 10,
    reportDownloadUrl: null,
  });

  const refreshStatus = useCallback(async () => {
    if (!sessionToken) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const data = await apiClient.getSelfInspectionStatus(sessionToken);
      setState(prev => ({
        ...prev,
        loading: false,
        status: data.status,
        totalFiles: data.total_files || 0,
        maxFiles: data.max_files || 10,
        reportDownloadUrl: data.report_download_url ?? null,
      }));
    } catch (err: unknown) {
      const errorMessage = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail || 'Failed to load status';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  }, [sessionToken]);

  useEffect(() => {
    if (!sessionToken || !autoRefresh) return;

    // Initial load
    refreshStatus();

    // Auto-refresh every 5 seconds
    const interval = setInterval(refreshStatus, 5000);

    return () => clearInterval(interval);
  }, [sessionToken, autoRefresh, refreshStatus]);

  return {
    ...state,
    refreshStatus,
  };
}

// ==================== useSelfInspectionSubmit Hook ====================

/**
 * Hook for submitting self-inspection
 * Triggers final submission and AI processing
 */
export function useSelfInspectionSubmit(sessionToken: string | null) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const submit = useCallback(async () => {
    if (!sessionToken) {
      setError('Session token is required');
      return false;
    }

    setSubmitting(true);
    setError(null);

    try {
      await apiClient.submitSelfInspection(sessionToken);
      setSubmitting(false);
      setSubmitted(true);
      return true;
    } catch (err: unknown) {
      const errorMessage = (err as {response?: {data?: {detail?: string}}}).response?.data?.detail || 'Failed to submit inspection';
      setSubmitting(false);
      setError(errorMessage);
      return false;
    }
  }, [sessionToken]);

  return {
    submitting,
    error,
    submitted,
    submit,
  };
}
